import path from 'path';

import { Entry } from 'contensis-management-api';

import TagsMigrationService from './TagsMigrationService';
import { DownloadStatus, MigrateRequest, MigrateStatus } from '~/models';
import { AssetEntryBuilder } from '~/transformations/AssetEntryBuilder';
import { EntryBuilder } from '~/transformations/EntryBuilder';
import { mapMigrateResult } from '~/transformations/mapEntriesMigrateResult';
import {
  Logger,
  completeProgressBar,
  logError,
  newProgressBar,
} from '~/util/logger';

class ContentAssetsMigrationService extends TagsMigrationService {
  download;
  created;
  updated;
  published;
  deleted;
  // localAssetsFolder: string;
  downloaded = [] as [string, DownloadStatus, MappedError | undefined][];

  constructor(payload: MigrateRequest, preview: boolean, download = false) {
    super(payload, preview);
    this.download = preview === false ? true : download;
    this.created = [] as string[];
    this.updated = [] as string[];
    this.published = [] as string[];
    this.deleted = [] as string[];
  }

  MigrateContentAssets = async () => {
    const { download, isPreview } = this;

    // Loads content types and components into memory
    await this.HydrateContensisRepositories([], false);

    // Loads all entries from source and target repos into memory
    await this.GetAssetEntriesToMigrate();

    // Build and record entries to migrate
    // against each targetRepo.entries.migrate
    await this.BuildTargetEntries();

    if ((download || !isPreview) && this.errorCount && !this.ignoreErrors) {
      Logger.error(
        `[assets] Not committing migration due to ${this.errorCount} errors`
      );
    } else if (download) {
      if (this.ignoreErrors)
        Logger.warning(
          `[assets] Committing migration with ${this.errorCount} known errors`
        );
      await this.DownloadAndQueueContentAssets(isPreview);
    }

    Logger.info(`Assets migration ${isPreview ? 'preview ready' : 'complete'}`);
    return mapMigrateResult(this, this.targets);
  };

  GetAssetEntriesToMigrate = async () => {
    const { source, targets } = this;

    // Get entries from source repo
    await source.assets.GetAssetEntries();

    for (const [, target] of Object.entries(targets)) {
      // Get entries from target repo
      void this.queue.add(() => target.assets.GetAssetEntries());
    }

    // Wait until we've got all repo entries
    await this.queue.onIdle();
  };

  BuildTargetEntries = async () => {
    // Build entries for each target repo
    for (const [, target] of Object.entries(this.targets)) {
      // We need all the asset entries built first so we can
      // search for existing asset ids from matches on file path
      await target.assets.BuildAssetEntries(this.payload.outputProgress);
      await target.entries.BuildMigrateEntries({
        outputProgress: this.payload.outputProgress,
        ignoreErrors: this.ignoreErrors,
        onError: (b) => {
          this.AddError(b.sourceEntry.sys.id || '', b.error);
        },
      });
    }
  };

  DownloadAndQueueContentAssets = async (isPreview: boolean) => {
    // Download asset content from entries data
    Logger.info(
      `[assets] Downloading content assets from project '${this.source.project.id}'`
    );

    const assetEntries = this.CheckAssetEntriesToDownload();

    const queuedEntries: AssetEntryBuilder[] = [];
    const unqueuedEntries: AssetEntryBuilder[] = [];
    // Process the first asset inside each unique folder outside of concurrent queue due to avoid CMS error
    // SQL ERROR: "Cannot insert duplicate key row in object 'dbo.F_Folders' with unique index
    // 'IND_UnqueFolders'. The duplicate key value is (130, 4586, image-library, 0)"
    for (const built of assetEntries) {
      if (
        unqueuedEntries.some(
          (e) =>
            (e.sourceEntry.sys as any)?.properties?.filePath ===
            (built.sourceEntry.sys as any)?.properties?.filePath
        )
      ) {
        queuedEntries.push(built);
      } else {
        unqueuedEntries.push(built);
      }
    }
    const pr = newProgressBar(
      ` Loading assets :bar :percent ${Logger.infoText(
        ':current/:total :elapseds/:etas :ratep/s'
      )}`,
      {
        complete: Logger.successText('='),
        head: Logger.successText('>>'),
        output: this.payload.outputProgress,
        total: assetEntries.length,
      }
    );
    Logger.info(
      `[assets] Processing ${unqueuedEntries.length} first assets in each folder separately in project '${this.source.project.id}'`
    );
    for (const sourceEntry of unqueuedEntries) {
      await this.DownloadAndCreateContentAsset(sourceEntry, isPreview);
      pr?.tick();
    }

    Logger.info(
      `[assets] Queue remaining ${queuedEntries.length} content assets in project '${this.source.project.id}'`
    );
    for (const sourceEntry of queuedEntries) {
      void this.queue.add(async () => {
        await this.DownloadAndCreateContentAsset(sourceEntry, isPreview);
        pr?.tick();
      });
    }

    await this.queue.onIdle();
    completeProgressBar();
  };

  DownloadAndCreateContentAsset = async (
    built: AssetEntryBuilder,
    isPreview: boolean
  ) => {
    const [error, status] = await built.downloadAssetContent();
    this.downloaded.push([built.assetLocalPath, status, error]);

    for (const [, target] of Object.entries(this.targets)) {
      const migrateEntry = target.entries.migrate?.find(
        (me) => me.originalId === built.originalId
      ) as AssetEntryBuilder | undefined;

      if (migrateEntry) {
        // record any download error in all targets
        if (status === 'error') {
          migrateEntry.status = 'error';
          migrateEntry.error = error;
        } else if (!isPreview) {
          // queue the asset upload
          Logger.info(
            `[assets] uploading [${target.projectId}:${built.contentTypeId}] | ${built.entryTitle} | ${built.originalId}`
          );
          await this.UploadAssetEntry(migrateEntry);
        }
      }
    }
  };

  UploadAssetEntry = async (migrateEntry: AssetEntryBuilder) => {
    const { contensis } = migrateEntry;
    const upsertEntry = migrateEntry.toJSON();

    // This could be the url we downloaded the file from
    // and may not be the same as the upload parent node/path
    delete upsertEntry.sys.uri;

    const [error, assetEntry, hasCreated, hasUpdated] =
      await contensis.repo.UpsertContentAsset(
        migrateEntry.existing,
        upsertEntry,
        migrateEntry.assetLocalPath,
        path.dirname(migrateEntry.filePath || '')
      );

    if (!error && assetEntry) {
      if (hasCreated && assetEntry.sys.id) {
        this.created.push(assetEntry.sys.id);
        migrateEntry.result = 'created';
      }
      if (hasUpdated && assetEntry.sys.id) {
        this.updated.push(assetEntry.sys.id);
        migrateEntry.result = 'updated';
      }
    }
    if (error) {
      const mappedError = this.AddError(`${upsertEntry.sys.id}`, error);
      migrateEntry.error = mappedError;
      migrateEntry.result = 'errors';
      logError(
        error,
        `${mappedError.message} | ${upsertEntry.sys.id} | ${upsertEntry.sys.contentTypeId}`
      );
    }
  };

  ShouldProcess = (status: MigrateStatus) =>
    !['error', 'not found', 'no change', 'ignore'].includes(status);

  CheckAssetEntriesToDownload = () => {
    const sourceEntries = this.source.entries.raw.filter(
      (e) => e.sys.dataFormat === 'asset'
    );

    const assets: AssetEntryBuilder[] = [];

    // Download asset content from entries data
    for (const sourceEntry of sourceEntries) {
      let processAsset: AssetEntryBuilder | undefined;
      for (const [, contensis] of Object.entries(this.targets)) {
        processAsset = contensis.entries.migrate?.find(
          (me) =>
            me.originalId === sourceEntry.sys.id &&
            this.ShouldProcess(me.status)
        ) as AssetEntryBuilder;

        if (processAsset) {
          assets.push(processAsset);
          break; // we only need to find the asset one time from one target
        }
      }
    }

    return assets;
  };

  DeleteAssetEntries = async (repos = this.targets) => {
    const { isPreview } = this;

    // Loads all entries from source and target repos into memory
    await this.GetAssetEntriesToDelete(repos);

    if (!isPreview) {
      for (const [projectId, target] of Object.entries(repos)) {
        Logger.info(`Deleting asset entries in project '${projectId}'`);

        const existingEntries = (target.entries.migrate
          ?.filter(
            (me) => me.status === 'delete' && typeof me.existing !== 'undefined'
          )
          .map((me) => me.existing) || []) as Entry[];

        for (const [i, entry] of Object.entries(existingEntries)) {
          void this.queue.add(async () => {
            Logger.warning(
              `[delete] ${Number(i) + 1}/${
                target.entries.raw.length
              } ${projectId} [${entry.sys.contentTypeId}] ${entry.entryTitle} ${
                entry.sys.id
              }`
            );
            try {
              if (entry.sys.id) {
                await target.repo.DeleteEntry(entry.sys.id);
                this.deleted.push(entry.sys.id);
              }
            } catch (error: any) {
              Logger.error(
                `Error attempting to delete asset entry ${
                  entry.sys.id
                }. Error was ${JSON.stringify(error)}`
              );
              this.AddError(`${entry.sys.id}`, error);
            }
          });
        }
      }
    }

    await this.queue.onIdle();

    Logger.info(`Done deleting asset entries`);
    return mapMigrateResult(this, repos);
  };

  GetAssetEntriesToDelete = async (repos = this.targets) => {
    const { source } = this;
    const originalVersionStatus = source.query?.versionStatus;

    // If a source is supplied we will read entries from here and pass the
    // transformed ids to the GetEntries call when fetching entries to delete
    // from the target repos
    if (source.repo?.ProjectId() && Object.keys(repos).length) {
      // Ensure we always select latest content when deleting
      if (source.query && !source.query.versionStatus)
        source.query.versionStatus = 'latest';

      // Get entries from source repo (if we haven't got them in a previous operation)
      if (source.entries.raw.length > 0) {
        // TODO: stash original entries list in migrate without dependencies so we can reuse it here

        // Dump existing source entries as they contain dependencies which we DON'T want to delete
        source.entryIds = [];
        source.entries.raw = [];
      }
      await source.assets.GetAssetEntries();
    }

    const promises = [] as Promise<any>[];

    for (const [, target] of Object.entries(repos)) {
      // Ensure we always select latest content when deleting
      if (target.query && !target.query.versionStatus)
        target.query.versionStatus = 'latest';

      // Fetch entries from target repo based on transformed entry ids
      // If there isn't any source entries to transform GetEntries will
      // fetch based on the query set in the target repo
      const entryIds = Object.fromEntries(
        source.entries.raw.map((e) => [
          target.transformGuid(e.sys.id || ''),
          e.sys.id,
        ])
      );

      promises.push(
        (async () => {
          const deleteEntries = await target.assets.GetAssetEntries(
            Object.keys(entryIds)
          );

          deleteEntries.map((e) => {
            const entryBuilder = new EntryBuilder(target, source, e);
            entryBuilder.status = 'delete';
            target.entries.migrate.push(entryBuilder);
          });
        })()
      );
    }

    // Get all repo entries at the same time
    await Promise.all(promises);

    // Reset the original versionStatus as this affects the tests by
    // altering the query in the requests from source causing it to run
    // the test with a different payload
    if (source.query) source.query.versionStatus = originalVersionStatus;
  };
}

export default ContentAssetsMigrationService;
